/* eslint-disable no-param-reassign */
import React from "react";
import PropTypes from "prop-types";
import css from 'styled-jsx/css';
import Heading from '@lmig/dotcom-aspect-components/Utility/Heading';
import TextBody from '@lmig/dotcom-aspect-components/Utility/TextBody';
import PhoneNumber from '@lmig/dotcom-aspect-components/Utility/PhoneNumber';
import { IconBusiness, IconPhone, IconAgent, IconClock, IconLocations, Link, Accordion, AccordionTab } from '@lmig/lmds-react';
import WrapperSizeable from "./Utility/WrapperSizeable";
import Rating from './Rating';
import Dot from './Dot';

const { className: linkClass, styles: linkStyles } = css.resolve`
  *{
    margin-left: 1rem;
  }
`;

const { className: headingClass, styles: headingStyles } = css.resolve`
    *{
      color: #1a1446;
      font-size: 1.5rem;
      line-height: 1.33;
      margin: 0 auto;
      display: block;

      @media screen and (min-width: 48rem) {
        font-size: 2rem;
        line-height: 1.25;
      }
    }
  `;

const { className: dayInfoClass, styles: dayInfoStyles } = css.resolve`
  .lm-Body{
    margin-left: 1rem; 
    margin-right: auto;
  }
`;

const { className: hourInfoClass, styles: hourInfoStyles } = css.resolve`
  .lm-Body{
    margin-left: auto; 
    margin-right: 0;
  }
`;

const getAdditionalStreetStyles = (streetInfo, getDirections) => {
  if (streetInfo === '' && getDirections) {
    return css.resolve`
    .lm-Body{
      margin-bottom: 2rem;
    }
  `;
  }

  if (streetInfo && getDirections) {
    return css.resolve`
    .lm-Body{
      margin-bottom: 0.5rem;
    }
  `;
  }

  if (streetInfo === '') {
    return css.resolve`
      .lm-Body{
        margin-bottom: 1.7rem;
      }
    `;
  }

  return { className: '', styles: '' };
};

const formatHours = (hours = {}) => {
  let { start, end } = hours;
  const { isClosed, byAppointmentOnly } = hours;

  if (isClosed) {
    return (
      <TextBody>
        Closed
      </TextBody>
    );
  }

  if (!!start && !!end) {
    start = start.toUpperCase();
    end = end.toUpperCase();

    return (
      <TextBody>
        {start} - {end}
      </TextBody>
    );
  }

  if (byAppointmentOnly) {
    return (
      <TextBody>
        By Appointment Only
      </TextBody>
    );
  }

  return null;
};

// days are ordered to support get day
const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const daysAbrv = ['Mon', 'Tues', 'Weds', 'Thurs', 'Fri', 'Sat', 'Sun'];

const renderIfOpen = (dailyHours) => {
  const dayObj = new Date();
  let day = days[(dayObj.getDay() + 6) % 7];
  const dayInfo = dailyHours[day];
  const { start, end } = dayInfo;

  if (!!start && !!end) {
    const endTimes = end.split(':');
    const endTime = parseInt(endTimes[0], 10) + 12;

    const startTimes = start.split(':');
    const startTime = parseInt(startTimes[0], 10);

    day = day.charAt(0).toUpperCase() + day.slice(1);

    if (dayObj.getHours() <= endTime && dayObj.getHours() >= startTime) {
      return (
        `Open Now - Closes at ${end.toUpperCase()}`
      );
    }

    return (
      `Closed - Opens at ${start.toUpperCase()} ${day}`
    );
  }

  return (
    `Currently by Appointment Only`
  );
};

const renderHours = (dailyHours) => {
  const arrayHours = Object.entries(dailyHours).sort((a, b) => days.indexOf(a[0]) - days.indexOf(b[0]));
  const headline = renderIfOpen(dailyHours);
  const allWeekdaysTheSame = [];
  const nonWeekdays = ['sunday', 'saturday'];
  let sameTimeWeekday = false;
  let sameWeekends = false;
  const weekdays = Object.keys(dailyHours).filter(day => !nonWeekdays.includes(day)).reduce((obj, key) => {
    obj[key] = dailyHours[key];

    return obj;
  }, {});
  const weekends = Object.keys(dailyHours).filter(day => nonWeekdays.includes(day)).reduce((obj, key) => {
    obj[key] = dailyHours[key];

    return obj;
  }, {});

  Object.keys(weekdays).forEach((day) => {
    const dayToCompare = weekdays[day];
    const results = Object.keys(weekdays).map((day2) => {
      const dayObj = weekdays[day2];

      return JSON.stringify(dayObj) === JSON.stringify(dayToCompare);
    });

    if (results.every(val => val === results[0])) {
      allWeekdaysTheSame.push(true);
    }
  });

  if (allWeekdaysTheSame.length === 5 && allWeekdaysTheSame.every(val => val === allWeekdaysTheSame[0])) {
    sameTimeWeekday = true;
  }

  if (JSON.stringify(weekends.saturday) === JSON.stringify(weekends.sunday)) {
    sameWeekends = true;
  }

  return (
    <div>
      <Accordion noBorderTop className="accordionWrapper" id="accordionWrapper">
        <AccordionTab labelVisual={headline} className='officeAccord' id="officeAccord">
          {sameTimeWeekday ? <div className="hoursOfOperation">
            <TextBody className={dayInfoClass}>
              Mon - Fri
            </TextBody>
            <TextBody className={hourInfoClass}>
              {formatHours(arrayHours[0][1])}
            </TextBody>
          </div> : <>
            {Object.keys(weekdays).map((day, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className="hoursOfOperation">
                <TextBody className={dayInfoClass}>
                  {daysAbrv[index]}
                </TextBody>
                <TextBody className={hourInfoClass}>
                  {formatHours(weekdays[day])}
                </TextBody>
              </div>
            ))}
          </>}
          {sameWeekends ? <div className="hoursOfOperation">
            <TextBody className={dayInfoClass}>
              Sat - Sun
            </TextBody>
            <TextBody className={hourInfoClass}>
              {formatHours(arrayHours[6][1])}
            </TextBody>
          </div>
            :
            <>
              <div className="hoursOfOperation">
                <TextBody className={dayInfoClass}>
                  Sat
                </TextBody>
                <TextBody className={hourInfoClass}>
                  {formatHours(arrayHours[5][1])}
                </TextBody>
              </div>
              <div className="hoursOfOperation">
                <TextBody className={dayInfoClass}>
                  Sun
                </TextBody>
                <TextBody className={hourInfoClass}>
                  {formatHours(arrayHours[6][1])}
                </TextBody>
              </div>
            </>}
        </AccordionTab>
      </Accordion>
      {dayInfoStyles}
      {hourInfoStyles}
      <style jsx>{`
        .hoursOfOperation {
          display: flex;
          font-size: .875rem;
        }
      `}
      </style>
    </div>
  );
};

const createURL = (address, alias) => {
  const state = address.state.name.toLowerCase();
  const url = `/insurance-agent/${state}/${alias}`;

  return url;
};

const OfficeCard = ({ address, phones, rating, reviewCount, dailyHours, alias, agentLink = false, getDirections = false, pin = undefined }) => {
  const path = createURL(address, alias);
  const { className: addStreetClass, styles: addStreetStyles } = getAdditionalStreetStyles(address.additionalStreetInfo, getDirections);

  return (
    <>
      <div id="officeCard">
        <div className="entityInfo">
          {pin && <div className="pin">
            <IconLocations label={pin.distanceAway && `${pin.distanceAway.toFixed(2)} mi`} />
            <span className="index">{String.fromCharCode(pin.index + 65)}</span>
          </div>}
          <Heading type="h4-bold" tag="h4">
            <Link href={path} variant="navigational">
              {address.city}, {address.state.code}
            </Link>
          </Heading>
          <div className="addressBlock">
            <div className="addressLogo">
              <IconBusiness size="16" />
            </div>
            <div className="addressInfo">
              <TextBody>
                {address.street}
              </TextBody>
              <TextBody>
                {address.additionalStreetInfo}
              </TextBody>
              <TextBody className={addStreetClass}>
                {address.city}, {address.state.code} {address.zip}
              </TextBody>
            </div>
          </div>
          {getDirections && <div className="agentLink">
            <IconLocations size="16" />
            <Link className={linkClass} href={`https://www.google.com/maps/dir//Comparion+Insurance+Agency+${address.street}`} variant="standalone" caretPosition="none" aria-label={`Get directions to the ${address?.city} office`} target="_blank">Get directions</Link>
          </div>}
          {phones?.primary &&
            <div className="phoneNumberBlock">
              <IconPhone size="16" />
              <span>
                <PhoneNumber tel={phones.primary.number}>
                  <Link
                    variant="standalone"
                    caretPosition="none"
                    className={linkClass}
                    aria-label="Contact us"
                  />
                </PhoneNumber>
              </span>
            </div>
          }
          {!!agentLink && (
            <div className="agentLink">
              <IconAgent size="16" />
              <Link className={linkClass} href={`${path}#agentsInOffice`} variant="standalone" caretPosition="none" aria-label={`View agents in the ${address?.city} office`}>View agents</Link>
            </div>
          )}
          {rating !== 0 && reviewCount !== 0 && <Rating rating={rating} count={reviewCount} />}
          {dailyHours ? (
            <>
              <div className="hoursBlock">
                <div className="hoursLogo">
                  <IconClock size="16" />
                </div>
              </div>
              {renderHours(dailyHours)}
            </>
          ) : (
            <div className="hoursBlock">
              <div className="hoursLogo">
                <IconClock size="16" />
              </div>
              <div className="hoursInfo">
                Call for Daily Hours
              </div>
            </div>
          )}
        </div>
      </div>
      <style global jsx>{`@import './OfficesByState.scoped.scss';`}</style>
      {addStreetStyles}
      {linkStyles}
    </>
  );
};

const NearbyEntity = ({
  entityData = [],
  title = false,
  titleText = 'Our offices'
}) => (
  <WrapperSizeable>
    {!!title && (
      <div className="officeResultsHeader">
        <Heading type="h2-bold" tag="h3" className={headingClass}>{titleText}</Heading>
        <Dot color="#1a1446" fill inlineMargin="0 0 1rem 0" />
      </div>
    )}
    <div className="officeResultsWrapper">
      {entityData.map(({ address, phones, rating, reviews, dailyHours, alias }, index) => (
        <OfficeCard key={alias} address={address} phones={phones} rating={rating} reviewCount={reviews?.length} dailyHours={dailyHours} alias={alias} shadow agentLink pin={{ index }} />
      ))}
    </div>
    {headingStyles}
    <style jsx>{`
      @import './global-styles/tokens';
      .officeResultsHeader {
        display: flex;
        flex-direction: column;
        align-items: center;
        max-width: 80rem;
      }
      .officeResultsWrapper{
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;
        @media screen and (min-width: $lm-breakpoint-md-min) {
          grid-template-columns: 1fr 1fr;
        }
        @media screen and (min-width: 1024px) {
          grid-template-columns: 1fr 1fr 1fr;
        }
      }
    `}</style>
  </WrapperSizeable>
);

NearbyEntity.propTypes = {
  entityData: PropTypes.array,
  title: PropTypes.bool,
  titleText: PropTypes.string,
};

export default NearbyEntity;
export { OfficeCard };
